/**
 * MeetYourBuddiesSection — public-facing showcase of the 6 Study Buddies.
 *
 * Design intent:
 * - This *replaces* the floating live buddy on the landing page (which was
 *   removed because it broke the marketing hierarchy). Marketing now leads
 *   the buddy story; the live buddy only kicks in once the kid signs in.
 * - 6 cute portrait cards, each tilts/lifts on hover, tier-coloured ring,
 *   "alive" badge ribbons (Coming soon: 3D, Live now, etc.).
 * - A *real* Rive-rigged demo strip below the cards proves what "next-gen
 *   alive" actually looks like — zero generative AI, runs at 60fps.
 */
import React, { useRef, useState } from 'react';
import { Sparkles, Star, Crown } from 'lucide-react';
import { BUDDY_ROSTER } from '@/components/BuddyRoster';

const TIER_BADGE = {
  basic:   { label: 'Free for everyone', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  growth:  { label: 'Growth tier',       cls: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  premium: { label: 'Elite tier',        cls: 'bg-amber-100 text-amber-700 border-amber-200' },
};
const TIER_ICON = { basic: Sparkles, growth: Star, premium: Crown };

function BuddyCard({ buddy, index }) {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  const onMove = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
    const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
    setTilt({ x: Math.max(-8, Math.min(8, -dy * 14)), y: Math.max(-8, Math.min(8, dx * 14)) });
  };
  const onLeave = () => setTilt({ x: 0, y: 0 });

  const tier = buddy.unlock_tier || 'basic';
  const badge = TIER_BADGE[tier] || TIER_BADGE.basic;
  const Icon = TIER_ICON[tier] || Sparkles;

  return (
    <div className="relative [perspective:1000px]"
         style={{ animationDelay: `${index * 80}ms` }}
         data-testid={`buddy-card-${buddy.id}`}>
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        className="relative rounded-3xl bg-white border border-slate-100 shadow-md hover:shadow-2xl transition-all duration-300 overflow-hidden [transform-style:preserve-3d] hover:-translate-y-1"
        style={{ transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)` }}>
        {/* Coloured halo backdrop */}
        <div
          className="absolute -inset-6 opacity-25 blur-3xl pointer-events-none transition-opacity duration-500 group-hover:opacity-50"
          style={{ background: `radial-gradient(circle at 50% 30%, ${buddy.accent}55, transparent 70%)` }}
        />

        {/* Portrait */}
        <div className="relative aspect-[4/5] overflow-hidden">
          <img
            src={buddy.image_url}
            alt={buddy.name}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            style={{ transform: 'translateZ(20px)' }}
          />
          {/* Tier ribbon */}
          <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-full border text-[10px] font-heading font-semibold flex items-center gap-1 backdrop-blur-sm ${badge.cls}`}
               style={{ transform: 'translateZ(40px)' }}>
            <Icon className="w-3 h-3" />
            {badge.label}
          </div>
        </div>

        {/* Caption */}
        <div className="p-5" style={{ transform: 'translateZ(10px)' }}>
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <p className="font-display text-lg font-bold text-slate-900 tracking-tight">{buddy.name}</p>
            <span className="text-[10px] text-slate-400 font-body uppercase tracking-wider">#{String(index + 1).padStart(2, '0')}</span>
          </div>
          <p className="text-xs font-heading uppercase tracking-wider mb-2"
             style={{ color: buddy.accent }}>{buddy.tagline}</p>
          <p className="text-sm text-slate-600 font-body leading-relaxed line-clamp-3">{buddy.description}</p>
        </div>
      </div>
    </div>
  );
}

export default function MeetYourBuddiesSection() {
  const buddies = ['chirpy', 'bhalu', 'saathi', 'unico', 'redpanda', 'wolfy', 'cutiepie', 'drago']
    .map(id => BUDDY_ROSTER[id]).filter(Boolean);

  return (
    <section id="buddies" className="relative py-24" data-testid="buddies-section">
      {/* Section eyebrow */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-rose-50 border border-rose-100 text-rose-700 text-xs font-heading mb-5">
            <Sparkles className="w-3.5 h-3.5" />
            Your study buddies
          </div>
          <h2 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1] mb-4">
            Meet the friends who'll
            <span className="bg-gradient-to-r from-rose-500 via-pink-500 to-amber-500 bg-clip-text text-transparent"> learn alongside you</span>.
          </h2>
          <p className="text-base sm:text-lg text-slate-600 font-body max-w-2xl mx-auto leading-relaxed">
            Six cute companions who cheer when you nail a quiz, sit quietly when you're stuck, and never judge a single doubt.
            Unlock new buddies as you grow into bigger plans.
          </p>
        </div>

        {/* The 6 buddies grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5" data-testid="buddies-grid">
          {buddies.map((b, i) => <BuddyCard key={b.id} buddy={b} index={i} />)}
        </div>
      </div>
    </section>
  );
}
